import { useMemo } from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { LogOut, UserRound } from 'lucide-react'
import { Alert, AppShell, Button, EMBLEM_URL, useAuth } from '@concours/ui'
import { buildNavigation } from '../navigation'
import { RouterLink } from '../lib/RouterLink'
import { useMyPermissions } from '../lib/permissions'

function UserActions() {
  const { t } = useTranslation()
  const { user, session } = useAuth()
  const navigate = useNavigate()
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email

  return (
    <>
      <Link className="btn btn-ghost" to="/compte" title={t('user.account')}>
        <UserRound aria-hidden="true" />
        <span className="hidden sm:inline">{name}</span>
        <span className="sr-only sm:hidden">{t('user.account')}</span>
      </Link>
      <Button
        variant="ghost"
        iconOnly
        icon={<LogOut />}
        aria-label={t('user.logout')}
        title={t('user.logout')}
        onClick={async () => {
          await session.logout()
          navigate('/connexion', { replace: true })
        }}
      />
    </>
  )
}

export function AdminLayout() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const nav = useMemo(() => buildNavigation(t), [t])

  return (
    <AppShell
      brand={{ name: t('app.name'), sub: t('app.sub'), logo: EMBLEM_URL }}
      nav={nav}
      currentPath={pathname}
      linkComponent={RouterLink}
      topbarTitle={t('app.title')}
      topbarActions={<UserActions />}
    >
      <MfaGate />
    </AppShell>
  )
}

/**
 * Un profil exige la MFA et elle n'est pas activée : l'API refuse toutes les
 * routes protégées (E_MFA_SETUP_REQUIRED) → on conduit vers « Mon compte ».
 */
function MfaGate() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const permissions = useMyPermissions()
  const blocked = Boolean(permissions.data?.mfaSetupRequired)
  if (blocked && pathname !== '/compte') return <Navigate to="/compte" replace />
  // Structure stable : lever la bannière ne doit pas remonter la page (codes de secours affichés).
  return (
    <>
      {blocked && (
        <Alert tone="warning" title={t('rbac.mfaGate.title')} className="mb-6">
          {t('rbac.mfaGate.text')}
        </Alert>
      )}
      <Outlet />
    </>
  )
}

/** Mise en page des écrans d'authentification (sans menu). */
export function AuthLayout() {
  const { t } = useTranslation()
  return (
    <div className="public-shell">
      <header className="public-header">
        <Link to="/" className="sidebar-brand">
          <img className="brand-logo" src={EMBLEM_URL} alt="" aria-hidden="true" />
          <span className="brand-text">
            <span className="brand-name">{t('app.name')}</span>
            <span className="brand-sub">{t('app.sub')}</span>
          </span>
        </Link>
      </header>
      <div className="flag-band" aria-hidden="true" />
      <main className="content" id="contenu">
        <Outlet />
      </main>
    </div>
  )
}
